import React, { useEffect, useState } from "react";
import { Box, Button, Container, TextField, Typography, Select, MenuItem, FormControl, Paper, TableContainer, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../store/Store";
import { FetchDataFailureAction, FetchDataSuccessAction, fetchData, patchData } from "../../store/actions";
import { getReportingManager, setReportingManager, viewUsers } from "../../Service/Apis";
import { ToastVariant, useCustomToast } from "../../components/Methods/SnackBar";

interface UserOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  employee_code: string;
  roles: string[];
}

const AssignManager: React.FC = () => {
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [managerId, setManagerId] = useState<string>("");
  const [currentManager, setCurrentManager] = useState<string | null>(null);
  const navigate = useNavigate();
  const [openDeletedIndex, setOpenDeletedIndex] = useState<number | null>(null);
  const showToast = useCustomToast();

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const list = await dispatch(fetchData(viewUsers));
        setUsers(list || []);
      } catch (e) {
        setUsers([]);
      }
    };
    loadUsers();
  }, [dispatch]);

  useEffect(() => {
    const loadCurrent = async () => {
      if (selectedEmployeeIds.length === 0) return;
      try {
        const lastId = selectedEmployeeIds[selectedEmployeeIds.length - 1];
        const rm = await dispatch(fetchData(`${getReportingManager}?employee_id=${lastId}`));
        setCurrentManager(rm?.reporting_manager_name || null);
      } catch (e) {
        setCurrentManager(null);
      }
    };
    loadCurrent();
  }, [dispatch, selectedEmployeeIds]);

  const save = async () => {
    if (selectedEmployeeIds.length === 0) return;
    try {
      await Promise.all(
        selectedEmployeeIds.map((id) =>
          dispatch(
            patchData(setReportingManager, {
              employee_id: id,
              manager_id: managerId || null,
            } as any)
          )
        )
      );
      const lastId = selectedEmployeeIds[selectedEmployeeIds.length - 1];
      const rm = await dispatch(fetchData(`${getReportingManager}?employee_id=${lastId}`));
      setCurrentManager(rm?.reporting_manager_name || null);
      showToast("Manager assignment saved", "success", true);
      navigate("/assign-manager");
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.message ||
        data?.detail ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        "Assignment failed";
      showToast(msg, "error", true);
    }
  };
  
  const managerOptions = users.filter(u => (u.roles || []).some(r => r?.toLowerCase() === "manager"));
  const employeeOptions = users
    .filter((u) => (u.roles || []).some((r) => ["employee", "hr"].includes(r?.toLowerCase())))
    .filter((u) => (!managerId || u.id !== managerId) && !selectedEmployeeIds.includes(u.id));
  const selectedEmployees = users.filter((u) => selectedEmployeeIds.includes(u.id));

  useEffect(() => {
    if (managerId && selectedEmployeeIds.includes(managerId)) {
      setSelectedEmployeeIds((prev) => prev.filter((id) => id !== managerId));
    }
  }, [managerId]);

  return (
    <Box sx={{ mt: 12, mb: 10 }}>
      <Box sx={{ m: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", m: 1, p: 1 }}>
          <Typography variant="h4" fontWeight={"bold"}>Assign Manager</Typography>
        </Box>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); save(); }} sx={{ display: "flex", justifyContent: "center", m: 1, p: 2 }}>
          <FormControl>
            <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
              <Typography paragraph sx={{ p: 0.5, width: "9rem", color: "black" }}>
                Manager :
              </Typography>
              <Select
                id="manager"
                size="small"
                value={managerId}
                onChange={(e: any) => setManagerId(e.target.value)}
                sx={{ width: "15rem" }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {managerOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.first_name} {m.last_name}({m.employee_code})
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
              <Typography paragraph sx={{ p: 0.5, width: "9rem" }}>
                Employee :
              </Typography>
              <Select
                id="employee"
                size="small"
                value=""
                onChange={(e: any) => {
                  const value = e.target.value as string;
                  if (value && !selectedEmployeeIds.includes(value)) {
                    setSelectedEmployeeIds((prev) => [...prev, value]);
                  }
                }}
                sx={{ width: "15rem" }}
              >
                {employeeOptions.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}({e.employee_code})
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {selectedEmployees.length > 0 && (
              <Box
                sx={{
                  m: 1,
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TableContainer component={Paper} sx={{ width: "fit-content" }}>
                  <TableHead className="header-background">
                    <TableRow>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                        Delete
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedEmployees.map((emp, index) => (
                      <TableRow key={emp.id}>
                        <TableCell sx={{ textAlign: "center" }}>
                          {emp.first_name} {emp.last_name}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <Button size="small" onClick={() => setOpenDeletedIndex(index)}>
                            Delete
                          </Button>
                          <Dialog
                            open={openDeletedIndex === index}
                            onClose={() => setOpenDeletedIndex(null)}
                            sx={{ m: 1, "& .MuiDialog-paper": { width: "30rem" } }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
                              <IconButton onClick={() => setOpenDeletedIndex(null)}>
                                <CloseIcon />
                              </IconButton>
                            </Box>
                            <DialogTitle>Confirmation</DialogTitle>
                            <DialogContent>
                              <Typography>Do you want to remove this employee?</Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button onClick={() => setOpenDeletedIndex(null)}>No</Button>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setSelectedEmployeeIds((prev) => prev.filter((id) => id !== emp.id));
                                  setOpenDeletedIndex(null);
                                }}
                              >
                                Yes
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableContainer>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "right", m: 1, p: 1 }}>
              <Button size="small" onClick={() => navigate(-1)} sx={{ m: 1 }}>
                Back
              </Button>
              <Button type="submit" variant="contained" size="small" sx={{ m: 1 }} disabled={!managerId || selectedEmployeeIds.length === 0}>
                Assign
              </Button>
            </Box>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ m: 2, display: "flex", alignItems: "center" }}>
            <Typography fontWeight={"bold"} sx={{ width: "9rem" }}>
              Current Manager :
            </Typography>
            <TextField
              variant="standard"
              size="small"
              inputProps={{ readOnly: true }}
              value={currentManager || "Unassigned"}
              sx={{ width: "15rem" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AssignManager;
